import React, { useEffect, useState, useCallback } from "react";
import api, { BACKEND_URL } from "@/lib/api";
import { Section } from "@/components/app/Primitives";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { toast } from "sonner";

const REPORTS = [
  { key: "spend-by-campaign", label: "Spend by Campaign", supportsPeriod: true, description: "Grouped by tenant × campaign, aggregated per period." },
  { key: "kys-pipeline", label: "KYS Pipeline", supportsPeriod: false, description: "All influencers in scope with their KYS + verification status." },
  { key: "payment-ageing", label: "Payment Ageing", supportsPeriod: false, description: "Payments bucketed by age (0-30 / 31-60 / 61-90 / 90+ days)." },
];

export default function Reports() {
  const [reportKey, setReportKey] = useState("spend-by-campaign");
  const [period, setPeriod] = useState("monthly");
  const [tenantId, setTenantId] = useState("");
  const [tenants, setTenants] = useState([]);
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/tenants")
      .then((r) => setTenants((r.data || []).filter((t) => !t.is_propay && !t.is_network && !t.is_audit_partner)))
      .catch(() => {});
  }, []);

  const load = useCallback(async () => {
    setBusy(true);
    try {
      const params = new URLSearchParams();
      params.set("fmt", "json");
      if (REPORTS.find((r) => r.key === reportKey)?.supportsPeriod) params.set("period", period);
      if (tenantId) params.set("tenant_id", tenantId);
      const r = await api.get(`/reports/${reportKey}?${params.toString()}`);
      setData(r.data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to load report");
    } finally {
      setBusy(false);
    }
  }, [reportKey, period, tenantId]);

  useEffect(() => { load(); }, [load]);

  const download = async (fmt) => {
    const params = new URLSearchParams();
    params.set("fmt", fmt);
    if (REPORTS.find((r) => r.key === reportKey)?.supportsPeriod) params.set("period", period);
    if (tenantId) params.set("tenant_id", tenantId);
    try {
      const resp = await fetch(`${BACKEND_URL}/api/reports/${reportKey}?${params.toString()}`, { credentials: "include" });
      if (!resp.ok) throw new Error(await resp.text());
      const blob = await resp.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${reportKey}${REPORTS.find((r) => r.key === reportKey)?.supportsPeriod ? `-${period}` : ""}.${fmt}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success(`${fmt.toUpperCase()} downloaded`);
    } catch (err) {
      toast.error(err?.message || "Download failed");
    }
  };

  const current = REPORTS.find((r) => r.key === reportKey);
  const columns = data?.columns || [];
  const rows = data?.rows || [];

  return (
    <div className="space-y-6" data-testid="reports-page">
      <Section
        title="Reports"
        subtitle="Weekly, monthly and quarterly rollups. Export to CSV for Excel or PDF for the board deck."
      />

      {/* Report picker */}
      <div className="grid md:grid-cols-3 gap-3">
        {REPORTS.map((r) => (
          <button
            key={r.key}
            type="button"
            onClick={() => setReportKey(r.key)}
            className={`text-left rounded-xl border-2 p-4 transition ${reportKey === r.key ? "border-[#0052FF] bg-[#0052FF]/5" : "border-slate-200 hover:border-slate-300"}`}
            data-testid={`report-picker-${r.key}`}
          >
            <div className={`text-xs font-bold uppercase tracking-widest ${reportKey === r.key ? "text-[#0052FF]" : "text-slate-500"}`}>{r.label}</div>
            <div className="text-xs text-slate-600 mt-1">{r.description}</div>
          </button>
        ))}
      </div>

      {/* Filters + export */}
      <div className="flex flex-wrap items-end gap-3 border-t pt-4">
        {current?.supportsPeriod && (
          <div>
            <label className="text-xs text-slate-500 block mb-1">Period</label>
            <select className="h-9 rounded-md border border-input px-3 text-sm bg-white" value={period} onChange={(e) => setPeriod(e.target.value)} data-testid="report-period">
              <option value="weekly">Weekly</option>
              <option value="monthly">Monthly</option>
              <option value="quarterly">Quarterly</option>
            </select>
          </div>
        )}
        <div>
          <label className="text-xs text-slate-500 block mb-1">Tenant</label>
          <select className="h-9 rounded-md border border-input px-3 text-sm bg-white" value={tenantId} onChange={(e) => setTenantId(e.target.value)} data-testid="report-tenant">
            <option value="">All tenants</option>
            {tenants.map((t) => (<option key={t.id} value={t.id}>{t.name}</option>))}
          </select>
        </div>
        <div className="flex-1" />
        <Button variant="outline" onClick={() => download("csv")} data-testid="download-csv-btn">⤓ CSV</Button>
        <Button className="bg-[#0052FF] hover:bg-[#0043D9]" onClick={() => download("pdf")} data-testid="download-pdf-btn">⤓ PDF</Button>
      </div>

      {/* Report table */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <div>
            <CardTitle className="text-sm">{data?.title || current?.label}</CardTitle>
            <div className="text-xs text-slate-500 mt-0.5">{data?.subtitle}</div>
          </div>
          <Badge variant="outline" data-testid="report-row-count">{rows.length} row{rows.length !== 1 ? "s" : ""}</Badge>
        </CardHeader>
        <CardContent className="overflow-x-auto p-0">
          {busy ? (
            <div className="text-center text-sm text-slate-500 py-8">Loading…</div>
          ) : rows.length === 0 ? (
            <div className="text-center text-sm text-slate-500 py-8">No data for this filter.</div>
          ) : (
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-xs uppercase tracking-widest text-slate-500">
                <tr>
                  {columns.map((c) => (<th key={c} className="px-4 py-3 text-left">{c.replace(/_/g, " ")}</th>))}
                </tr>
              </thead>
              <tbody>
                {rows.map((r, i) => (
                  <tr key={columns.map((c) => String(r[c])).join("|")} className="border-t hover:bg-slate-50" data-testid={`report-row-${i}`}>
                    {columns.map((c) => (
                      <td key={c} className={`px-4 py-2 ${typeof r[c] === "number" ? "font-mono text-right" : ""}`}>
                        {typeof r[c] === "number" && c !== "age_days" && c !== "orders"
                          ? Number(r[c]).toLocaleString(undefined, { maximumFractionDigits: 2 })
                          : String(r[c] ?? "—")}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
